import React, { useState, useEffect, useMemo, useRef, useCallback, useId } from 'react';
import ReactDOM from 'react-dom';
import { V } from '../utils/theme';
import { LS } from '../utils/storage';
import { Icons } from './Icons';
import { Undo } from '../utils/undo';
import { isCardio, wUnit } from '../utils/helpers';
import { useLayout } from '../utils/responsive';
import { SessionManager } from '../utils/auth';
import { CodeInput, Notice, IconTile, onAccent } from './kit';

// ─── #2 Confirm Dialog ───
function ConfirmDialog({msg,detail,onConfirm,onCancel}){
  return(
    <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div style={{position:"absolute",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,.7)",backdropFilter:"blur(4px)"}} onClick={onCancel}/>
      <div role="alertdialog" aria-modal="true" aria-label={msg} style={{position:"relative",background:V.sheetBg,
        borderRadius:18,padding:24,maxWidth:320,width:"100%",border:`1px solid ${V.cardBorder}`,
        boxShadow:V.shadowLg,animation:"popIn .22s cubic-bezier(.2,.8,.3,1) both"}}>
        <div style={{fontSize:15,fontWeight:700,color:V.text,marginBottom:6}}>{msg}</div>
        {detail&&<div style={{fontSize:12,color:V.text3,lineHeight:1.5,marginBottom:16}}>{detail}</div>}
        <div style={{display:"flex",gap:10}}>
          <Btn v="secondary" full onClick={onCancel}>Cancel</Btn>
          <Btn full onClick={onConfirm} s={{background:V.danger,color:"#fff"}}>Delete</Btn>
        </div>
      </div>
    </div>
  );
}

// ─── #2 Undo Toast — with countdown bar ───
function UndoToast({d}){
  const [item,setItem]=useState(null);
  const [remaining,setRemaining]=useState(0);
  const UNDO_DURATION=6000;
  useEffect(()=>{
    const unregister=Undo.register({setItem,setRemaining,duration:UNDO_DURATION});
    return unregister;
  },[]);
  // Tick the countdown bar down in real-time
  useEffect(()=>{
    if(!item||remaining<=0)return;
    const iv=setInterval(()=>setRemaining(r=>Math.max(0,r-100)),100);
    return()=>clearInterval(iv);
  },[item,remaining>0]);
  if(!item)return null;
  const doUndo=()=>{
    if(!item)return;
    if(item.type==="workout")d({type:"ADD_W",w:item.item});
    else if(item.type==="nutrition")d({type:"ADD_N",n:item.item});
    else if(item.type==="body")d({type:"ADD_B",b:item.item});
    else if(item.type==="photo")d({type:"ADD_PHOTO",photo:item.item});
    Undo.pop();
    SuccessToastCtrl.show("Restored");
  };
  const pct=Math.round((remaining/UNDO_DURATION)*100);
  return ReactDOM.createPortal(
    <div style={{position:"fixed",bottom:"calc(96px + env(safe-area-inset-bottom))",left:"50%",transform:"translateX(-50%)",zIndex:1500,
      minWidth:220,background:V.mode==="dark"?"rgba(26,26,40,0.96)":"rgba(255,255,255,0.97)",borderRadius:14,
      border:`1px solid ${V.cardBorder}`,backdropFilter:"blur(16px)",
      overflow:"hidden",boxShadow:V.shadowLg,animation:"fadeInDown .25s ease-out"}}>
      <div style={{display:"flex",alignItems:"center",gap:10,padding:"10px 16px"}}>
        <span style={{fontSize:12,color:V.text,fontWeight:500}}>{item.label}</span>
        <button onClick={doUndo} style={{padding:"5px 14px",minHeight:36,minWidth:56,borderRadius:8,background:V.accent,border:"none",
          cursor:"pointer",fontSize:11,fontWeight:700,color:onAccent(),fontFamily:V.font,marginLeft:"auto",
          boxShadow:`0 2px 10px -2px ${V.accent}60`}}>Undo</button>
      </div>
      <div style={{height:3,background:V.divider}}>
        <div style={{height:"100%",background:`linear-gradient(90deg,${V.accent},${V.accent2})`,width:`${pct}%`,transition:"width .1s linear"}}/>
      </div>
    </div>,
    document.body
  );
}

// ─── Global Confirm Controller — replaces browser confirm() ───
let _confirmSetState=null;
const ConfirmCtrl={
  show:(msg,detail,onConfirm)=>{if(_confirmSetState)_confirmSetState({msg,detail,onConfirm});},
  clear:()=>{if(_confirmSetState)_confirmSetState(null);},
};
function GlobalConfirm(){
  const [state,setState]=useState(null);
  useEffect(()=>{_confirmSetState=setState;return()=>{_confirmSetState=null;};},[]);
  if(!state)return null;
  return ReactDOM.createPortal(
    <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,zIndex:9995,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div style={{position:"absolute",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,.7)",backdropFilter:"blur(4px)"}} onClick={()=>ConfirmCtrl.clear()}/>
      <div role="alertdialog" aria-modal="true" aria-label={state.msg} style={{position:"relative",background:V.sheetBg,
        borderRadius:18,padding:24,maxWidth:320,width:"100%",border:`1px solid ${V.cardBorder}`,
        boxShadow:V.shadowLg,animation:"popIn .22s cubic-bezier(.2,.8,.3,1) both"}}>
        <div style={{fontSize:15,fontWeight:700,color:V.text,marginBottom:6}}>{state.msg}</div>
        {state.detail&&<div style={{fontSize:12,color:V.text3,lineHeight:1.5,marginBottom:16}}>{state.detail}</div>}
        <div style={{display:"flex",gap:10}}>
          <Btn v="secondary" full onClick={()=>ConfirmCtrl.clear()}>Cancel</Btn>
          <Btn full onClick={()=>{state.onConfirm();ConfirmCtrl.clear();}} s={{background:V.danger,color:"#fff"}}>Confirm</Btn>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── Session Re-auth Modal ───
// A server session lasts 30 days; when it lapses while the user keeps using the app
// locally, every authenticated request 401s and social/sync silently dead-end. This
// re-creates the session from the account PIN WITHOUT wiping local data — previously
// the only way past a dead session was the destructive "Erase Everything" reset.
let _reauthSetOpen=null;
const ReauthCtrl={
  show:()=>{if(_reauthSetOpen)_reauthSetOpen(true);},
  clear:()=>{if(_reauthSetOpen)_reauthSetOpen(false);},
};
function ReauthModal(){
  const [open,setOpen]=useState(false);
  const [pin,setPin]=useState("");
  const [busy,setBusy]=useState(false);
  const [err,setErr]=useState("");
  useEffect(()=>{
    _reauthSetOpen=(v)=>{setOpen(v);if(v){setPin("");setErr("");setBusy(false);}};
    return()=>{_reauthSetOpen=null;};
  },[]);
  if(!open)return null;
  const email=LS.get("ft-session-email")||(LS.get("ft-profile")||{}).email||"";
  const submit=async()=>{
    if(pin.length!==6||busy)return;
    setBusy(true);setErr("");
    const res=await SessionManager.create(email,pin,LS.get("ft-device-id"));
    if(res&&res.success){
      SuccessToastCtrl.show("Signed back in");
      // Reload so every screen re-fetches with the fresh token. Local data is already
      // persisted to localStorage, so nothing is lost.
      setTimeout(()=>window.location.reload(),400);
    }else{
      setBusy(false);
      setErr(res&&res.error?res.error
        :(res&&res.attempts_remaining!=null?`Wrong PIN — ${res.attempts_remaining} attempt${res.attempts_remaining===1?"":"s"} left`
        :"Could not sign in. Try again."));
    }
  };
  return ReactDOM.createPortal(
    <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,zIndex:9996,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div style={{position:"absolute",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,.7)",backdropFilter:"blur(4px)"}} onClick={()=>ReauthCtrl.clear()}/>
      <div role="dialog" aria-modal="true" aria-label="Session expired" style={{position:"relative",background:V.sheetBg,
        borderRadius:18,padding:24,maxWidth:340,width:"100%",border:`1px solid ${V.cardBorder}`,
        boxShadow:V.shadowLg,animation:"popIn .22s cubic-bezier(.2,.8,.3,1) both"}}>
        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:14}}>
          <IconTile icon={Icons.hash} size={42}/>
          <div style={{minWidth:0}}>
            <div style={{fontSize:16,fontWeight:800,color:V.text,letterSpacing:"-0.01em"}}>Session expired</div>
            <div style={{fontSize:12,color:V.text3,lineHeight:1.5,marginTop:2}}>
              Enter your 6-digit PIN to sign back in{email?<> as <span style={{color:V.text,fontWeight:600}}>{email}</span></>:""}. Your data is saved on this device.
            </div>
          </div>
        </div>
        <CodeInput value={pin} onChange={v=>{setPin(v);setErr("");}} secret autoFocus autoComplete="current-password" name="pin"
          onEnter={submit} state={err?"error":undefined} ariaLabel="Account PIN"/>
        <Notice tone="error" center style={{marginBottom:err?12:4,marginTop:-4}}>{err}</Notice>
        <div style={{display:"flex",gap:10}}>
          <Btn v="secondary" full onClick={()=>ReauthCtrl.clear()}>Cancel</Btn>
          <Btn full disabled={pin.length!==6||busy} onClick={submit}>{busy?"Signing in…":"Sign In"}</Btn>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── Message Banner System (iMessage-style in-app notifications) ───
let _msgBannerQueue=[];
let _msgBannerTimer=null;
const MsgBannerCtrl={
  push:(msg)=>{
    _msgBannerQueue.push({...msg,id:Date.now()+Math.random(),ts:Date.now()});
    if(_msgBannerQueue.length>5)_msgBannerQueue=_msgBannerQueue.slice(-5);
  },
  pop:()=>{
    if(_msgBannerQueue.length===0)return null;
    const oldest=_msgBannerQueue[0];
    if(Date.now()-oldest.ts>5000){_msgBannerQueue.shift();return null;}
    return oldest;
  },
  dismiss:()=>{_msgBannerQueue.shift();},
  clear:()=>{_msgBannerQueue=[];},
};

function MessageBanner({onTap}){
  const [banner,setBanner]=useState(null);
  const [show,setShow]=useState(false);
  useEffect(()=>{
    const iv=setInterval(()=>{
      const msg=MsgBannerCtrl.pop();
      if(msg&&!banner){setBanner(msg);setShow(true);
        setTimeout(()=>{setShow(false);setTimeout(()=>{setBanner(null);MsgBannerCtrl.dismiss();},300);},4000);
      }
    },500);
    return()=>clearInterval(iv);
  },[banner]);
  if(!banner)return null;
  return ReactDOM.createPortal(
    <div onClick={()=>{if(onTap)onTap(banner);setShow(false);setTimeout(()=>{setBanner(null);MsgBannerCtrl.dismiss();},100);}}
      style={{position:"fixed",top:0,left:0,right:0,zIndex:9998,padding:"8px 16px",
        paddingTop:"max(12px, env(safe-area-inset-top, 8px))",
        background:V.mode==="dark"?"rgba(18,18,30,0.95)":"rgba(255,255,255,0.96)",backdropFilter:"blur(20px) saturate(160%)",
        borderBottom:`1px solid ${V.cardBorder}`,cursor:"pointer",
        transform:show?"translateY(0)":"translateY(-100%)",transition:"transform .3s cubic-bezier(.32,.72,.33,1)",
        boxShadow:V.shadowLg}}>
      <div style={{display:"flex",alignItems:"center",gap:10}}>
        <div style={{width:36,height:36,borderRadius:10,
          background:banner.type==="group"?`linear-gradient(135deg,${V.purple},#ec4899)`:`linear-gradient(135deg,${V.accent},${V.accent2})`,
          display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
          <span style={{fontSize:14,color:V.bg,fontWeight:900}}>{(banner.name||"?")[0].toUpperCase()}</span>
        </div>
        <div style={{flex:1,minWidth:0}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <span style={{fontSize:13,fontWeight:700,color:V.text}}>{banner.name}</span>
            <span style={{fontSize:9,color:V.text3}}>now</span>
          </div>
          <div style={{fontSize:12,color:V.text3,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
            {banner.type==="group"&&<span style={{color:V.purple,fontSize:10}}>#{banner.group} · </span>}
            {banner.text}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── Success Toast System ───
let _successMsg=null;let _successTimer=null;
let _successSetState=null;
// show(msg) for a success; show(msg,{tone:"error"}) or error(msg) for a failure (danger colour, ✕)
const SuccessToastCtrl={
  show:(msg,opts)=>{const tone=opts?.tone==="error"?"error":"success";if(_successTimer)clearTimeout(_successTimer);if(_successSetState)_successSetState({msg,tone,ts:Date.now()});_successTimer=setTimeout(()=>{if(_successSetState)_successSetState(null);},tone==="error"?4000:2500);},
  error:(msg)=>SuccessToastCtrl.show(msg,{tone:"error"}),
  dismiss:()=>{if(_successTimer)clearTimeout(_successTimer);if(_successSetState)_successSetState(null);},
};

function SuccessToast(){
  const [msg,setMsg]=useState(null);
  useEffect(()=>{
    _successSetState=setMsg;
    return()=>{_successSetState=null;};
  },[]);
  const err=msg?.tone==="error";const c=err?V.danger:V.accent;
  // The live region stays mounted so screen readers announce each new message
  return ReactDOM.createPortal(
    <div role="status" aria-live="polite" aria-atomic="true">
    {msg&&(
    <div data-tone={err?"error":"success"} style={{position:"fixed",top:"calc(64px + env(safe-area-inset-top))",left:"50%",transform:"translateX(-50%)",zIndex:1500,
      padding:"10px 18px",background:V.mode==="dark"?"rgba(14,24,20,0.92)":"rgba(255,255,255,0.96)",
      borderRadius:14,border:`1px solid ${c}38`,
      display:"flex",alignItems:"center",gap:9,
      boxShadow:`0 8px 28px -6px ${c}35, 0 4px 20px rgba(0,0,0,0.25)`,
      backdropFilter:"blur(16px)",animation:"fadeInDown .25s ease-out"}}>
      <div aria-hidden="true" style={{width:20,height:20,borderRadius:10,background:err?V.danger:`linear-gradient(135deg,${V.accent},${V.accent2})`,
        display:"flex",alignItems:"center",justifyContent:"center",boxShadow:`0 0 10px ${c}60`}}>
        <span style={{color:err?"#fff":onAccent(),fontSize:12,fontWeight:900}}>{err?"✕":"✓"}</span>
      </div>
      <span style={{fontSize:13,fontWeight:600,color:V.text}}>{msg.msg}</span>
    </div>)}
    </div>,
    document.body
  );
}

// ─── #3 Pre-save Validation ───
function validateWorkout(w,s){
  const warnings=[];
  w.exercises.forEach(ex=>{
    const name=s.exercises.find(e=>e.id===ex.exerciseId)?.name||ex.exerciseId;
    const cardio=isCardio(ex.exerciseId,s.exercises);
    ex.sets.forEach(st=>{
      if(cardio){
        if(st.duration>300)warnings.push(`${name}: ${st.duration} min seems extremely long`);
      }else{
        if(st.weight>800)warnings.push(`${name}: ${st.weight} ${wUnit(s.units)} seems extremely high`);
        if(st.reps>100)warnings.push(`${name}: ${st.reps} reps seems high`);
      }
    });
  });
  // Duplicate check
  // Exclude the workout itself (by id) when checking for duplicates — editing path
  const dupes=s.workouts.filter(wk=>wk.id!==w.id&&wk.date===w.date&&wk.exercises.map(e=>e.exerciseId).sort().join(",")===w.exercises.map(e=>e.exerciseId).sort().join(","));
  if(dupes.length>0)warnings.push("You already logged a similar workout today");
  return warnings;
}
function validateNutrition(n){
  const w=[];
  if(n.cal>8000)w.push(`${n.cal} calories seems extremely high`);
  if(n.protein>500)w.push(`${n.protein}g protein seems very high`);
  return w;
}
function validateBody(b,s){
  const w=[];
  const last=s.body[0];
  if(last&&b.weight>0&&Math.abs(b.weight-last.weight)>20)
    w.push(`Weight change of ${Math.abs(b.weight-last.weight).toFixed(1)} ${wUnit(s.units)} from last entry`);
  return w;
}

// ─── #3 Validation Confirm Dialog ───
function ValidationWarning({warnings,onConfirm,onCancel}){
  if(!warnings||warnings.length===0)return null;
  return(
    <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div style={{position:"absolute",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,.7)"}} onClick={onCancel}/>
      <div role="alertdialog" aria-modal="true" style={{position:"relative",background:V.sheetBg,
        borderRadius:18,padding:24,maxWidth:340,width:"100%",border:`1px solid ${V.warn}30`,
        boxShadow:V.shadowLg,animation:"popIn .22s cubic-bezier(.2,.8,.3,1) both"}}>
        <div style={{fontSize:15,fontWeight:700,color:V.warn,marginBottom:8}}>⚠️ Heads up</div>
        <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:16}}>
          {warnings.map((w,i)=><div key={i} style={{fontSize:12,color:V.text2,lineHeight:1.4,padding:"6px 0",borderBottom:`1px solid ${V.divider}`}}>• {w}</div>)}
        </div>
        <div style={{display:"flex",gap:10}}>
          <Btn v="secondary" full onClick={onCancel}>Go Back</Btn>
          <Btn full onClick={onConfirm}>Save Anyway</Btn>
        </div>
      </div>
    </div>
  );
}

// ─── #5 Searchable Exercise Picker ───
const LEVEL_DOT={1:"#22c55e",2:"#f59e0b",3:"#f43f5e"};
// Equipment tags available for each onboarding "where do you train" answer
const EQUIP_SETS={home:["Dumbbell","Bodyweight","Band","Kettlebell","Other"],bodyweight:["Bodyweight","Band"],machines:["Machine","Cable","Smith","Bodyweight"]};
// `gym` (optional): the gym's own exercises (catalog shape, ids 'org:<id>'), listed first under
// "Your gym". Picking one calls `onPickGym(ex)` so the caller can keep a copy in s.exercises.
function ExercisePicker({exercises,value,onChange,catFilter,setCatFilter,todayType,gym=[],onPickGym}){
  const [search,setSearch]=useState("");
  const gymIds=useMemo(()=>new Set(gym.map(g=>g.id)),[gym]);
  const own=useMemo(()=>gymIds.size?exercises.filter(e=>!gymIds.has(e.id)):exercises,[exercises,gymIds]);
  // "Mine" = only equipment the user said they have at signup (full gym → everything, no chip)
  const myGear=EQUIP_SETS[(LS.get("ft-profile")||{}).equipment]||null;
  const [equipFilter,setEquipFilter]=useState(myGear?"Mine":"All");
  const cats=["All",...new Set([...gym,...own].map(e=>e.cat))];
  const equips=["All",...(myGear?["Mine"]:[]),...new Set([...gym,...own].map(e=>e.equip).filter(Boolean))];
  const recent=LS.get("ft-recent-exercises")||[];
  // Split-to-category mapping for suggestions
  const splitCats={Push:["Chest","Shoulders","Triceps"],Pull:["Back","Biceps"],Legs:["Legs","Glutes"],
    "Upper Power":["Chest","Back","Shoulders"],"Lower Power":["Legs","Glutes"],"Upper Hyper":["Chest","Back","Biceps","Triceps"],
    "Lower Hyper":["Legs","Glutes"],Chest:["Chest"],Back:["Back"],Shoulders:["Shoulders"],Arms:["Biceps","Triceps"]};
  const applyFilters=useCallback((list)=>{
    if(catFilter&&catFilter!=="All")list=list.filter(e=>e.cat===catFilter);
    if(equipFilter==="Mine"&&myGear)list=list.filter(e=>!e.equip||myGear.includes(e.equip));
    else if(equipFilter!=="All")list=list.filter(e=>e.equip===equipFilter);
    if(search)list=list.filter(e=>e.name.toLowerCase().includes(search.toLowerCase()));
    return list;
  },[catFilter,equipFilter,search,myGear]);
  const gymFiltered=useMemo(()=>applyFilters(gym),[gym,applyFilters]);
  const filtered=useMemo(()=>{
    let list=applyFilters(own);
    // Pin schedule-suggested then recent to top
    if(!search&&(!catFilter||catFilter==="All")&&(equipFilter==="All"||equipFilter==="Mine")){
      const sugCats=todayType?splitCats[todayType]:null;
      const sugExs=sugCats?list.filter(e=>sugCats.includes(e.cat)):[];
      const recentExs=recent.map(id=>own.find(e=>e.id===id)).filter(Boolean);
      const pinned=[...sugExs,...recentExs.filter(e=>!sugExs.some(s2=>s2.id===e.id))];
      const rest=list.filter(e=>!pinned.some(p=>p.id===e.id));
      list=[...pinned,...rest];
    }
    return list.slice(0,40);
  },[own,catFilter,equipFilter,search,recent,todayType,applyFilters]);

  const select=(id)=>{
    const g=gymIds.has(id)?gym.find(x=>x.id===id):null;
    if(g&&onPickGym)onPickGym(g);
    onChange(id);
    // Track recent
    const r=[id,...(recent||[]).filter(r2=>r2!==id)].slice(0,5);
    LS.set("ft-recent-exercises",r);
  };

  const renderRow=(ex,isGym)=>(
          <button key={ex.id} onClick={()=>select(ex.id)} aria-label={`Add ${ex.name}`}
            style={{display:"flex",alignItems:"center",gap:8,width:"100%",padding:"8px 10px",
              background:value===ex.id?`${V.accent}12`:"transparent",borderRadius:value===ex.id?8:0,
              border:"none",borderBottom:`1px solid ${V.divider}`,cursor:"pointer",textAlign:"left",WebkitTapHighlightColor:"transparent"}}>
            {ex.lvl&&<span title={["","Beginner","Intermediate","Advanced"][ex.lvl]} style={{width:6,height:6,borderRadius:3,
              background:LEVEL_DOT[ex.lvl]||V.text3,flexShrink:0}}/>}
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:12,color:value===ex.id?V.accent:V.text,fontWeight:value===ex.id?700:400}}>{ex.name}</div>
              <div style={{fontSize:9,color:V.text3}}>
                {ex.cat}{ex.equip?` · ${ex.equip}`:""}{recent.includes(ex.id)?" · Recent":""}
                {todayType&&splitCats[todayType]?.includes(ex.cat)&&!recent.includes(ex.id)?" · Today's split":""}
              </div>
            </div>
            {ex.yt&&<span style={{fontSize:8,color:V.text3}}>📹</span>}
            {isGym&&<span style={{fontSize:8,fontWeight:800,color:V.accent,letterSpacing:".06em"}}>GYM</span>}
          </button>
  );

  return(
    <div>
      <div style={{position:"relative",marginBottom:8}}>
        <div style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)"}}>{Icons.search({size:14,color:V.text3})}</div>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder={`Search exercises (${own.length+gym.length})...`}
          aria-label="Search exercises"
          style={{width:"100%",padding:"10px 14px 10px 34px",background:V.inputBg,border:`1px solid ${V.cardBorder}`,
            borderRadius:10,color:V.text,fontSize:13,outline:"none",boxSizing:"border-box",minHeight:40,fontFamily:V.font}}/>
      </div>
      <div style={{display:"flex",gap:4,flexWrap:"wrap",marginBottom:6}} role="tablist">
        {cats.map(c=><Chip key={c} label={c} active={(catFilter||"All")===c} onClick={()=>setCatFilter(c==="All"?null:c)}/>)}
      </div>
      {/* Equipment filter — horizontal scroll strip */}
      {equips.length>2&&(
        <div style={{display:"flex",gap:4,overflowX:"auto",paddingBottom:6,marginBottom:4,WebkitOverflowScrolling:"touch"}}>
          {equips.map(eq=>(
            <button key={eq} onClick={()=>setEquipFilter(eq)} style={{padding:"4px 10px",borderRadius:14,fontSize:10,
              fontWeight:600,fontFamily:V.font,cursor:"pointer",whiteSpace:"nowrap",flexShrink:0,WebkitTapHighlightColor:"transparent",
              border:`1px solid ${equipFilter===eq?`${V.accent2}45`:"transparent"}`,
              background:equipFilter===eq?`${V.accent2}16`:V.hoverBg,
              color:equipFilter===eq?V.accent2:V.text3}}>
              {eq==="All"?"🏋️ All equipment":eq==="Mine"?"✓ My gear":eq}
            </button>
          ))}
        </div>
      )}
      <div style={{maxHeight:220,overflowY:"auto",WebkitOverflowScrolling:"touch"}}>
        {gymFiltered.length>0&&(
          <div role="group" aria-label="Your gym" data-testid="picker-gym">
            <div style={{fontSize:10,fontWeight:800,color:V.accent,letterSpacing:".08em",textTransform:"uppercase",padding:"8px 10px 4px"}}>Your gym</div>
            {gymFiltered.map(ex=>renderRow(ex,true))}
            {filtered.length>0&&<div style={{fontSize:10,fontWeight:800,color:V.text3,letterSpacing:".08em",textTransform:"uppercase",padding:"10px 10px 4px"}}>All exercises</div>}
          </div>
        )}
        {filtered.map(ex=>renderRow(ex,false))}
        {filtered.length===0&&gymFiltered.length===0&&<div style={{textAlign:"center",padding:14,color:V.text3,fontSize:12}}>No exercises match</div>}
      </div>
    </div>
  );
}

// ─── Shared Mobile Components ───
// Enter/Space on a focused clickable Card acts like a click (it has role=button); keys
// pressed inside a child input or button are left alone.
const cardKey=(onClick)=>(e)=>{if(e.target!==e.currentTarget||(e.key!=="Enter"&&e.key!==" "))return;e.preventDefault();onClick(e);};
const Card = ({children, style:s={}, onClick, glow}) => (
  <div onClick={onClick} {...(onClick?{role:"button",tabIndex:0,className:"hover-lift",onKeyDown:cardKey(onClick)}:{})}
    style={{background:glow?V.cardGlow:V.cardGrad,border:`1px solid ${glow?`${V.accent}28`:V.cardBorder}`,
    borderRadius:16,padding:16,boxShadow:V.shadow,transition:"background .2s, border-color .2s",
    ...(onClick?{cursor:"pointer",WebkitTapHighlightColor:"transparent"}:{}),...s}}>
    {children}
  </div>
);

// #38: Skeleton loading placeholders
const shimmerGrad=()=>V.mode==="dark"
  ?"linear-gradient(90deg,rgba(255,255,255,0.03) 25%,rgba(255,255,255,0.07) 50%,rgba(255,255,255,0.03) 75%)"
  :"linear-gradient(90deg,rgba(15,23,42,0.04) 25%,rgba(15,23,42,0.08) 50%,rgba(15,23,42,0.04) 75%)";
const Skeleton=({rows=3,h=14,gap=10})=>(
  <div style={{display:"flex",flexDirection:"column",gap}}>
    {Array.from({length:rows}).map((_,i)=>(
      <div key={i} style={{height:h,borderRadius:6,
        background:shimmerGrad(),
        backgroundSize:"400px 100%",animation:"shimmer 1.5s infinite linear"}}/>
    ))}
  </div>
);
const SkeletonCard=({lines=2})=>(
  <Card style={{padding:14}}>
    <div style={{display:"flex",gap:10,alignItems:"center",marginBottom:lines>1?10:0}}>
      <div style={{width:36,height:36,borderRadius:10,
        backgroundImage:shimmerGrad(),
        backgroundSize:"400px 100%",animation:"shimmer 1.5s infinite linear",flexShrink:0}}/>
      <div style={{flex:1}}><Skeleton rows={1} h={12}/></div>
    </div>
    {lines>1&&<Skeleton rows={lines-1} h={10} gap={6}/>}
  </Card>
);

const Btn = ({children,v="primary",full,disabled,onClick,s:extra={}}) => {
  const base = {display:"flex",alignItems:"center",justifyContent:"center",gap:6,borderRadius:12,border:"none",
    fontSize:14,fontWeight:600,fontFamily:V.font,cursor:disabled?"default":"pointer",opacity:disabled?.4:1,
    WebkitTapHighlightColor:"transparent",transition:"all .15s",minHeight:44,...(full?{width:"100%"}:{})};
  const vars = {
    primary:{...base,background:`linear-gradient(135deg,${V.accent},${V.accent2})`,color:onAccent(),padding:"12px 20px",
      fontWeight:700,boxShadow:disabled?"none":V.glow},
    secondary:{...base,background:V.hoverBg,color:V.text2,padding:"12px 20px",border:`1px solid ${V.cardBorder}`},
    danger:{...base,background:`${V.danger}14`,color:V.danger,padding:"12px 20px",border:`1px solid ${V.danger}28`},
    ghost:{...base,background:"transparent",color:V.text2,padding:"8px 12px"},
    small:{...base,background:`${V.accent}14`,color:V.accent,padding:"7px 13px",fontSize:12,borderRadius:10,minHeight:36,
      border:`1px solid ${V.accent}28`},
  };
  const styles={...(vars[v]||vars.primary),...extra};
  // Custom background replaces the variant's — drop the accent glow so they don't clash
  if(extra.background&&!extra.boxShadow&&styles.boxShadow===V.glow)styles.boxShadow="none";
  return <button onClick={disabled?undefined:onClick} style={styles}>{children}</button>;
};

const Field = ({label,value,onChange,type="text",unit,placeholder,min,max,step,autoFocus}) => {
  const [focus,setFocus]=useState(false);
  const id=useId();
  return(
  <div style={{marginBottom:12}}>
    {label&&<label htmlFor={id} style={{display:"block",fontSize:11,color:focus?V.accent:V.text3,textTransform:"uppercase",letterSpacing:".08em",
      marginBottom:6,fontWeight:600,transition:"color .15s"}}>{label}</label>}
    <div style={{position:"relative"}}>
      <input id={id} type={type} value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder}
        min={min} max={max} step={step} autoFocus={autoFocus}
        onFocus={()=>setFocus(true)} onBlur={()=>setFocus(false)}
        style={{width:"100%",padding:"12px 14px",paddingRight:unit?44:14,background:V.inputBg,
          border:`1px solid ${focus?`${V.accent}70`:V.cardBorder}`,borderRadius:12,color:V.text,fontSize:16,fontFamily:V.mono,
          outline:"none",boxSizing:"border-box",WebkitAppearance:"none",minHeight:44,
          boxShadow:focus?`0 0 0 3px ${V.accent}1a`:"none",transition:"border-color .15s, box-shadow .15s"}} />
      {unit&&<span style={{position:"absolute",right:14,top:"50%",transform:"translateY(-50%)",fontSize:11,
        color:focus?V.accent:V.text3,fontWeight:600,transition:"color .15s"}}>{unit}</span>}
    </div>
  </div>
  );
};

const Progress = ({val,max,color=V.accent,h=6}) => {
  const over=max>0&&val>max;
  return(
  <div style={{width:"100%",height:h,background:V.mode==="dark"?"rgba(255,255,255,0.06)":"rgba(15,23,42,0.07)",
    borderRadius:h,overflow:"hidden"}}>
    <div style={{width:`${max>0?Math.min(100,(val/max)*100):0}%`,height:"100%",
      background:over?`linear-gradient(90deg,${V.danger},${V.danger}cc)`:`linear-gradient(90deg,${color}dd,${color})`,
      borderRadius:h,transition:"width .4s ease",boxShadow:`0 0 ${h+4}px ${over?V.danger:color}50`,
      animation:"barFill .6s cubic-bezier(.2,.7,.3,1)"}}/>
  </div>
  );
};

// Sheet — portalled to document.body so iOS position:fixed works correctly
// (position:fixed inside -webkit-overflow-scrolling:touch is broken on iOS Safari)
// On desktop: renders as a centered modal with backdrop
const Sheet = ({title,onClose,children,footer}) => {
  const { isDesktop } = useLayout();
  return ReactDOM.createPortal(
  <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,zIndex:9990,
    background:isDesktop?"rgba(0,0,0,0.6)":V.sheetBg,
    display:"flex",flexDirection:"column",
    ...(isDesktop
      ?{alignItems:"center",justifyContent:"center",backdropFilter:"blur(8px)",animation:"fadeIn .2s ease-out"}
      :{animation:"slideUpSheet .32s cubic-bezier(.32,.72,.33,1)"}),
  }}
    role="dialog" aria-modal="true" aria-label={title}
    {...(isDesktop?{onClick:(e)=>{if(e.target===e.currentTarget)onClose();}}:{})}>
    <div style={{
      display:"flex",flexDirection:"column",
      ...(isDesktop?{maxWidth:700,width:"100%",maxHeight:"85vh",borderRadius:20,overflow:"hidden",
        background:V.sheetBg,border:`1px solid ${V.cardBorder}`,boxShadow:V.shadowLg,
        animation:"popIn .25s cubic-bezier(.2,.8,.3,1) both"}:
        {flex:1,minHeight:0,overflow:"hidden"}),
    }}>
      {/* Header */}
      <div className="sheet-head" style={{flexShrink:0,display:"flex",alignItems:"center",gap:12,
        paddingLeft:16,paddingRight:16,paddingBottom:12,borderBottom:`1px solid ${V.cardBorder}`,background:V.sheetBg,zIndex:2,
        ...(isDesktop?{paddingTop:16}:{}),
      }}>
        <button onClick={onClose} style={{display:"flex",alignItems:"center",gap:5,background:"none",border:"none",
          cursor:"pointer",padding:"8px 4px",WebkitTapHighlightColor:"transparent",flexShrink:0}}>
          {Icons.chevLeft({size:20,color:V.accent})}
          <span style={{fontSize:14,color:V.accent,fontWeight:600}}>Back</span>
        </button>
        <h3 style={{margin:0,fontSize:16,color:V.text,fontFamily:V.font,fontWeight:700,flex:1,textAlign:"center",paddingRight:50}}>{title}</h3>
      </div>
      {/* Scrollable content — flex:1 + minHeight:0 is the correct flex scroll pattern */}
      <div style={{flex:1,minHeight:0,overflowY:"auto",WebkitOverflowScrolling:"touch",
        overscrollBehavior:"contain",padding:20}}>
        {children}
      </div>
      {/* Footer — part of the flex column, never overlaps content */}
      {footer&&<div className="sheet-footer" style={{background:V.sheetBg}}>{footer}</div>}
    </div>
  </div>,
  document.body
);
};

const Chip = ({label,active,onClick,color}) => (
  <button onClick={onClick} style={{padding:"6px 13px",borderRadius:20,fontSize:12,fontWeight:600,
    fontFamily:V.font,cursor:"pointer",WebkitTapHighlightColor:"transparent",transition:"all .15s",
    border:`1px solid ${active?`${color||V.accent}45`:"transparent"}`,
    background:active?`${color||V.accent}16`:V.hoverBg,
    boxShadow:active?`0 2px 12px -3px ${color||V.accent}40`:"none",
    color:active?(color||V.accent):V.text3}}>
    {label}
  </button>
);

const Stat = ({icon,label,value,unit,color=V.accent,sub}) => (
  <Card>
    <div style={{display:"flex",alignItems:"center",gap:10}}>
      <div style={{width:40,height:40,borderRadius:12,background:`linear-gradient(135deg,${color}20,${color}08)`,
        border:`1px solid ${color}26`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
        {icon({size:17,color})}
      </div>
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontSize:10,color:V.text3,textTransform:"uppercase",letterSpacing:".06em",fontWeight:600}}>{label}</div>
        <div style={{fontSize:22,fontWeight:700,color:V.text,fontFamily:V.mono,lineHeight:1.2}}>
          {value}<span style={{fontSize:11,color:V.text3,marginLeft:3}}>{unit}</span>
        </div>
        {sub&&<div style={{fontSize:10,color:V.text3,marginTop:1}}>{sub}</div>}
      </div>
    </div>
  </Card>
);

export {
  validateWorkout,
  validateNutrition,
  validateBody,
  ValidationWarning,
  ExercisePicker,
  Card,
  Skeleton,
  SkeletonCard,
  Btn,
  Field,
  Progress,
  Sheet,
  Chip,
  Stat,
  ConfirmDialog,
  UndoToast,
  GlobalConfirm,
  ConfirmCtrl,
  MessageBanner,
  MsgBannerCtrl,
  SuccessToast,
  SuccessToastCtrl,
  ReauthModal,
  ReauthCtrl,
};
